import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button, Input, Link, MessageBox } from "@/components/ui";
import { usePasswordReset } from "@/hooks/usePasswordReset";
import { useToast } from "@/hooks/useToast";
import { AuthPageLayout } from "@/layouts/AuthPageLayout";
import { LOGIN_PAGE } from "@/router/paths";
const requestResetSchema = z.object({
    email: z.string().min(1, "Email is required").email("Please enter a valid email")
});
const confirmResetSchema = z.object({
    confirmationCode: z.string().transform((value)=>value.trim()).pipe(z.string().min(1, "Confirmation code is required")),
    newPassword: z.string().min(8, "Password must be at least 8 characters").regex(/[0-9]/, "Password must contain at least 1 number").regex(/[A-Z]/, "Password must contain at least 1 uppercase letter").regex(/[a-z]/, "Password must contain at least 1 lowercase letter").regex(/[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+\- ]/, "Password must contain at least 1 special character"),
    confirmPassword: z.string().min(1, "Please confirm your password")
}).refine((data)=>data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: [
        "confirmPassword"
    ]
});
export default function ForgotPasswordPage() {
    const [email, setEmail] = useState("");
    const [codeSent, setCodeSent] = useState(false);
    const [resetComplete, setResetComplete] = useState(false);
    const { forgotPasswordResponse, state, forgotPassword, confirmForgotPassword, resendCode } = usePasswordReset();
    const toast = useToast();
    const requestForm = useForm({
        resolver: zodResolver(requestResetSchema)
    });
    const confirmForm = useForm({
        resolver: zodResolver(confirmResetSchema)
    });
    const requiresLogin = forgotPasswordResponse?.requiresLogin === true;
    const showCodeForm = codeSent && !requiresLogin;
    useEffect(()=>{
        if (forgotPasswordResponse) {
            setCodeSent(true);
        }
    }, [
        forgotPasswordResponse
    ]);
    const onRequestReset = async (data)=>{
        try {
            await forgotPassword(data.email);
            setEmail(data.email);
            toast.success("Password reset instructions sent");
        } catch  {
            toast.error("Failed to send password reset code");
        }
    };
    const onConfirmReset = async (data)=>{
        try {
            await confirmForgotPassword({
                email,
                confirmationCode: data.confirmationCode.trim(),
                newPassword: data.newPassword
            });
            setResetComplete(true);
            toast.success("Password reset successfully");
        } catch  {
            toast.error("Failed to reset password");
        }
    };
    const onResendCode = async ()=>{
        if (!email) {
            return;
        }
        try {
            await resendCode(email);
            toast.success("Password reset code resent");
        } catch  {
            toast.error("Failed to resend password reset code");
        }
    };
    return /*#__PURE__*/ _jsx(AuthPageLayout, {
        title: "Reset Password",
        subtitle: resetComplete ? "Your password has been updated." : requiresLogin ? "Check your email for account setup instructions, then log in." : codeSent ? "Enter the code from your email and choose a new password." : "Enter your email and we will send password reset instructions.",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "w-full",
            children: [
                state.error && /*#__PURE__*/ _jsx(MessageBox, {
                    className: "mb-4",
                    message: state.error,
                    type: "error"
                }),
                state.success && !state.error && /*#__PURE__*/ _jsx(MessageBox, {
                    className: "mb-4",
                    message: state.success,
                    type: resetComplete ? "success" : "info"
                }),
                resetComplete || requiresLogin ? /*#__PURE__*/ _jsx("div", {
                    className: "mt-6",
                    children: /*#__PURE__*/ _jsx(Button, {
                        href: LOGIN_PAGE,
                        variant: "simple",
                        color: "navy-90",
                        underline: false,
                        className: "flex w-full justify-center",
                        children: "Back to Login"
                    })
                }) : showCodeForm ? /*#__PURE__*/ _jsxs("form", {
                    onSubmit: confirmForm.handleSubmit(onConfirmReset),
                    className: "w-full flex flex-col gap-4",
                    autoComplete: "off",
                    children: [
                        /*#__PURE__*/ _jsx("input", {
                            "aria-hidden": "true",
                            className: "absolute h-0 w-0 opacity-0 pointer-events-none",
                            tabIndex: -1,
                            type: "text",
                            name: "username",
                            autoComplete: "username",
                            value: email,
                            readOnly: true
                        }),
                        /*#__PURE__*/ _jsx(Input, {
                            label: "Confirmation Code",
                            type: "tel",
                            inputMode: "numeric",
                            autoComplete: "off",
                            autoCapitalize: "none",
                            pattern: "[0-9]*",
                            spellCheck: false,
                            placeholder: "Enter confirmation code",
                            ...confirmForm.register("confirmationCode"),
                            errors: confirmForm.formState.errors.confirmationCode
                        }),
                        /*#__PURE__*/ _jsx(Input, {
                            label: "New Password",
                            type: "password",
                            autoComplete: "new-password",
                            placeholder: "Enter your new password",
                            ...confirmForm.register("newPassword"),
                            errors: confirmForm.formState.errors.newPassword
                        }),
                        /*#__PURE__*/ _jsx(Input, {
                            label: "Confirm New Password",
                            type: "password",
                            autoComplete: "new-password",
                            placeholder: "Confirm your new password",
                            ...confirmForm.register("confirmPassword"),
                            errors: confirmForm.formState.errors.confirmPassword
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            type: "submit",
                            variant: "simple",
                            color: "navy-90",
                            className: "w-full",
                            disabled: state.isLoading || confirmForm.formState.isSubmitting,
                            children: state.isLoading || confirmForm.formState.isSubmitting ? "Resetting..." : "Reset Password"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            type: "button",
                            variant: "simple",
                            color: "navy-light",
                            className: "w-full",
                            onClick: onResendCode,
                            disabled: state.isLoading,
                            children: "Resend Code"
                        })
                    ]
                }) : /*#__PURE__*/ _jsxs("form", {
                    onSubmit: requestForm.handleSubmit(onRequestReset),
                    className: "w-full flex flex-col gap-4",
                    children: [
                        /*#__PURE__*/ _jsx(Input, {
                            label: "Email",
                            type: "email",
                            autoComplete: "email",
                            placeholder: "Enter your email",
                            ...requestForm.register("email"),
                            errors: requestForm.formState.errors.email
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            type: "submit",
                            variant: "simple",
                            color: "navy-90",
                            className: "w-full",
                            disabled: state.isLoading || requestForm.formState.isSubmitting,
                            children: state.isLoading || requestForm.formState.isSubmitting ? "Sending..." : "Send Reset Code"
                        })
                    ]
                }),
                !resetComplete && /*#__PURE__*/ _jsxs("p", {
                    className: "mt-6 text-center text-xs text-gray-500",
                    children: [
                        "Lost access to your authenticator app? Please",
                        " ",
                        /*#__PURE__*/ _jsx(Link, {
                            href: "mailto:support@qrypt.com",
                            external: true,
                            className: "text-xs",
                            children: "contact support"
                        }),
                        " ",
                        "for assistance."
                    ]
                }),
                !resetComplete && /*#__PURE__*/ _jsx("div", {
                    className: "mt-4 text-center",
                    children: /*#__PURE__*/ _jsx(Link, {
                        href: LOGIN_PAGE,
                        className: "text-sm",
                        children: "Back to login"
                    })
                })
            ]
        })
    });
}
